import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  CircularProgress,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Card,
  CardContent,
  Zoom,
  Divider,
  Fade,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Import icons
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TodayIcon from '@mui/icons-material/Today';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import TakeoutDiningIcon from '@mui/icons-material/TakeoutDining';
import LoungeIcon from '@mui/icons-material/Weekend';
import PaymentsIcon from '@mui/icons-material/Payments';
import CashIcon from '@mui/icons-material/AttachMoney';
import BankIcon from '@mui/icons-material/AccountBalance';
import CreditCardIcon from '@mui/icons-material/CreditCard';

// Import Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler,
} from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';

// Import local components
import Sidebar from '../../../../components/sidebars/Sidebar_hotelAccount_cashier';
import TopNav from '../../../../components/TopNav_hotelStaff';
import axios from 'axios';
import * as XLSX from 'xlsx';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler
);

const CATEGORY_COLORS = {
  'Beer': '#F59E0B',
  'Mocktail': '#10B981',
  'Cocktail': '#8B5CF6',
  'Kitchen/Restaurant': '#EF4444',
  'Bottle Water': '#3B82F6',
  'Soft Drinks': '#EC4899',
  'default': '#6B7280'
};

const STOCK_STATUS = {
  in_stock: { bg: alpha('#10B981', 0.15), color: '#10B981', label: 'In Stock', icon: CheckCircleIcon, glow: '0 0 10px rgba(16,185,129,0.3)' },
  low_stock: { bg: alpha('#F59E0B', 0.15), color: '#F59E0B', label: 'Low Stock', icon: WarningIcon, glow: '0 0 10px rgba(245,158,11,0.3)' },
  out_of_stock: { bg: alpha('#EF4444', 0.15), color: '#EF4444', label: 'Out of Stock', icon: ErrorIcon, glow: '0 0 10px rgba(239,68,68,0.3)' }
};

const InventoryAccounting = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [inventoryData, setInventoryData] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('current_stock');
  const [order, setOrder] = useState('asc');
  
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetailsOpen, setItemDetailsOpen] = useState(false);
  const [salesHistory, setSalesHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  const userProfile = JSON.parse(localStorage.getItem("userProfile"));
  const hotel_code = userProfile?.hotel_code || "";
  const hotelName = userProfile?.hotel_name || "Hotel";

  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/login');
  };

  const fetchInventoryData = async () => {
    if (!hotel_code) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_BASE_URL || '';
      const params = { 
        hotel_code,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      };
      
      const response = await axios.get(`${baseURL}/hotel/account/inventory-accounting.php`, { params });
      
      if (response.data && response.data.success) {
        setInventoryData(response.data.data);
      } else {
        setInventoryData({
          summary: {
            total_revenue: 0,
            total_items_sold: 0,
            total_inventory_items: 0,
            out_of_stock_items: 0,
            low_stock_items: 0,
            in_stock_items: 0,
            total_stock_units: 0
          },
          category_breakdown: [],
          inventory_items: [],
          top_selling_items: [],
          stock_alerts: [],
          today_transactions: [],
          order_type_report: [],
          payment_methods_report: []
        });
      }
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryData();
  }, [hotel_code, startDate, endDate]);

  const getDateRangeText = () => {
    if (startDate.toDateString() === endDate.toDateString()) {
      return `for ${startDate.toLocaleDateString()}`;
    }
    return `from ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`;
  };
  
  const filteredItems = useMemo(() => {
    if (!inventoryData?.inventory_items) return [];
    
    let filtered = [...inventoryData.inventory_items];
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category === categoryFilter);
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => {
        if (statusFilter === 'in_stock') return item.current_stock > item.min_stock;
        if (statusFilter === 'low_stock') return item.current_stock <= item.min_stock && item.current_stock > 0;
        if (statusFilter === 'out_of_stock') return item.current_stock === 0;
        return true;
      });
    }
    
    filtered.sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (orderBy === 'total_sold_amount' || orderBy === 'current_stock') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [inventoryData, searchTerm, categoryFilter, statusFilter, orderBy, order]);

  const paginatedItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleViewItemDetails = async (item) => {
    setSelectedItem(item);
    setItemDetailsOpen(true);
    setHistoryLoading(true);
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(`${baseURL}/hotel/account/inventory-accounting.php`, {
        params: { hotel_code, item_id: item.id, action: 'sales_history', days: 30 }
      });
      if (response.data.success) setSalesHistory(response.data.data);
    } catch (error) {
      console.error('Error fetching sales history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const exportToExcel = () => {
    const exportData = filteredItems.map(item => ({
      'Item Name': item.item_name,
      'Category': item.category,
      'Current Stock': `${item.current_stock} ${item.unit}`,
      'Min Stock': `${item.min_stock} ${item.unit}`,
      'Status': item.current_stock === 0 ? 'Out of Stock' : item.current_stock <= item.min_stock ? 'Low Stock' : 'In Stock',
      'Total Sold': item.total_sold_quantity || 0,
      'Revenue': `₦${(parseFloat(item.total_sold_amount) || 0).toLocaleString()}`,
      'Unit': item.unit
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
    XLSX.writeFile(wb, `inventory_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const summary = inventoryData?.summary || {};
  const categoryBreakdown = inventoryData?.category_breakdown || [];
  const topSellingItems = inventoryData?.top_selling_items || [];
  const stockAlerts = inventoryData?.stock_alerts || [];
  const todayTransactions = inventoryData?.today_transactions || [];
  const orderTypeReport = inventoryData?.order_type_report || [];
  const paymentMethodsReport = inventoryData?.payment_methods_report || [];

  const getStockStatus = (item) => {
    if (item.current_stock === 0) return STOCK_STATUS.out_of_stock;
    if (item.current_stock <= item.min_stock) return STOCK_STATUS.low_stock;
    return STOCK_STATUS.in_stock;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Beer': <LocalDrinkIcon />,
      'Mocktail': <LocalDrinkIcon />,
      'Cocktail': <LocalDrinkIcon />,
      'Kitchen/Restaurant': <RestaurantIcon />,
    };
    return icons[category] || <FastfoodIcon />;
  };

  const getOrderTypeIcon = (orderType) => {
    const icons = {
      'dine_in': <RestaurantIcon />,
      'room_service': <RoomServiceIcon />,
      'takeaway': <TakeoutDiningIcon />,
      'delivery': <DeliveryDiningIcon />,
      'Lounge': <LoungeIcon />
    };
    return icons[orderType] || <ReceiptIcon />;
  };

  const getPaymentMethodIcon = (method) => {
    const icons = {
      'Cash': <CashIcon />,
      'Bank Transfer': <BankIcon />,
      'POS Terminal': <CreditCardIcon />,
      'Credit Card': <CreditCardIcon />,
      'Mobile Money': <PaymentsIcon />,
      'Online Payment': <PaymentsIcon />
    };
    return icons[method] || <PaymentsIcon />;
  };

  if (!hotel_code) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Please log in again</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        
        <Box sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}>
          <TopNav onLogout={handleLogout} onNotificationClick={() => {}} toggleSidebar={toggleSidebar} />
          
          <Box sx={{ mb: 4, mt: 2 }}>
            <Typography variant="h4" fontWeight="800" sx={{ background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent' }}>
              Inventory Accounting
            </Typography>
            <Typography variant="body1" color="text.secondary">Track stock levels, sales, and revenue analysis</Typography>
          </Box>

          <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, background: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  sx={{ width: '100%' }}
                  slotProps={{ textField: { size: 'small', sx: { bgcolor: '#f8fafc', borderRadius: 2 } } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  sx={{ width: '100%' }}
                  slotProps={{ textField: { size: 'small', sx: { bgcolor: '#f8fafc', borderRadius: 2 } } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  onClick={fetchInventoryData}
                  startIcon={<RefreshIcon />}
                  fullWidth
                  sx={{ borderRadius: 2, py: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  Apply Filter
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <>
              <Grid container spacing={3} sx={{ mb: 4 }}>
               

                <Grid item xs={12} sm={6} md={3}>
                  <Zoom in style={{ transitionDelay: '100ms' }}>
                    <Paper sx={{ p: 3, borderRadius: 4, borderTop: '4px solid #10B981', boxShadow: '0 4px 15px rgba(0,0,0,0.08)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Inventory Items</Typography>
                          <Typography variant="h4" fontWeight={800}>{summary.total_inventory_items}</Typography>
                          <Typography variant="caption" color="text.secondary">{summary.total_stock_units} units total</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha('#10B981', 0.1), width: 56, height: 56, color: '#10B981' }}>
                          <InventoryIcon sx={{ fontSize: 32 }} />
                        </Avatar>
                      </Box>
                    </Paper>
                  </Zoom>
                </Grid>

                {startDate.toDateString() !== new Date().toDateString() && (
                  <Grid item xs={12}>
                    <Chip 
                      icon={<TodayIcon />}
                      label={`Showing data for: ${getDateRangeText()}`}
                      sx={{ bgcolor: alpha('#667eea', 0.1), color: '#667eea' }}
                    />
                  </Grid>
                )}
              </Grid>

              {stockAlerts.length > 0 && (
                <Fade in>
                  <Paper sx={{ mb: 3, p: 2, borderRadius: 3, background: `linear-gradient(135deg, ${alpha('#F59E0B', 0.1)} 0%, ${alpha('#F59E0B', 0.05)} 100%)`, border: `1px solid ${alpha('#F59E0B', 0.3)}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <WarningIcon sx={{ color: '#F59E0B' }} />
                      <Typography variant="subtitle1" fontWeight={700}>Stock Alerts</Typography>
                      <Chip label={`${stockAlerts.length} items need attention`} size="small" sx={{ bgcolor: '#F59E0B', color: 'white' }} />
                    </Box>
                    <Grid container spacing={1}>
                      {stockAlerts.slice(0, 6).map((alert, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <Chip
                            label={`${alert.item_name}: ${alert.current_stock} ${alert.unit} left (Min: ${alert.min_stock})`}
                            size="small"
                            sx={{ width: '100%', justifyContent: 'flex-start', bgcolor: alert.current_stock === 0 ? alpha('#EF4444', 0.1) : alpha('#F59E0B', 0.1), color: alert.current_stock === 0 ? '#EF4444' : '#F59E0B', fontWeight: 500 }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Fade>
              )}

              <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ px: 3, pt: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <Tab icon={<InventoryIcon />} iconPosition="start" label="Inventory Items" />
                  <Tab icon={<ShoppingCartIcon />} iconPosition="start" label="Transactions" />
                  <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Top Selling" />
                  <Tab icon={<ReceiptIcon />} iconPosition="start" label="Category Analysis" />
                  <Tab icon={<LocalDrinkIcon />} iconPosition="start" label="Order Outlets" />
                  <Tab icon={<AttachMoneyIcon />} iconPosition="start" label="Payment Methods" />
                </Tabs>

                {activeTab === 0 && (
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                      <TextField
                        placeholder="Search items..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ minWidth: 250, bgcolor: '#f8fafc', borderRadius: 2 }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                      />
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 150, bgcolor: '#f8fafc', borderRadius: 2 }}>
                          <InputLabel>Category</InputLabel>
                          <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} label="Category">
                            <MenuItem value="all">All Categories</MenuItem>
                            {[...new Set(inventoryData?.inventory_items?.map(i => i.category) || [])].map(cat => (
                              <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 130, bgcolor: '#f8fafc', borderRadius: 2 }}>
                          <InputLabel>Status</InputLabel>
                          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="in_stock">In Stock</MenuItem>
                            <MenuItem value="low_stock">Low Stock</MenuItem>
                            <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                          </Select>
                        </FormControl>
                        <Tooltip title="Export">
                          <IconButton onClick={exportToExcel} sx={{ bgcolor: '#f8fafc' }}><DownloadIcon /></IconButton>
                        </Tooltip>
                        <Tooltip title="Refresh">
                          <IconButton onClick={fetchInventoryData} sx={{ bgcolor: '#f8fafc' }}><RefreshIcon /></IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                            <TableCell>Item</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell align="right">
                              <TableSortLabel active={orderBy === 'current_stock'} direction={order} onClick={() => handleRequestSort('current_stock')}>
                                Stock
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">
                              <TableSortLabel active={orderBy === 'total_sold_quantity'} direction={order} onClick={() => handleRequestSort('total_sold_quantity')}>
                                Sold
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel active={orderBy === 'total_sold_amount'} direction={order} onClick={() => handleRequestSort('total_sold_amount')}>
                                Revenue
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedItems.map((item) => {
                            const status = getStockStatus(item);
                            const StatusIcon = status.icon;
                            const percentStock = (item.current_stock / (item.current_stock + item.min_stock)) * 100;
                            
                            return (
                              <TableRow key={item.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: alpha(CATEGORY_COLORS[item.category] || CATEGORY_COLORS.default, 0.1), color: CATEGORY_COLORS[item.category] || CATEGORY_COLORS.default }}>
                                      {getCategoryIcon(item.category)}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
                                      <Typography variant="caption" color="text.secondary">{item.unit}</Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip label={item.category} size="small" sx={{ bgcolor: alpha(CATEGORY_COLORS[item.category] || CATEGORY_COLORS.default, 0.1), color: CATEGORY_COLORS[item.category] || CATEGORY_COLORS.default }} />
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ minWidth: 100 }}>
                                    <Typography variant="body2" fontWeight={500}>{item.current_stock} {item.unit}</Typography>
                                    <LinearProgress variant="determinate" value={Math.min(percentStock, 100)} sx={{ height: 4, borderRadius: 2, mt: 0.5, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: status.color, borderRadius: 2 } }} />
                                    <Typography variant="caption" color="text.secondary">Min: {item.min_stock}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip icon={<StatusIcon sx={{ fontSize: 14 }} />} label={status.label} size="small" sx={{ bgcolor: status.bg, color: status.color, fontWeight: 500 }} />
                                </TableCell>
                                <TableCell align="right">{item.total_sold_quantity || 0} units</TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={700} color="primary.main">₦{(parseFloat(item.total_sold_amount) || 0).toLocaleString()}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title="View Details">
                                    <IconButton size="small" onClick={() => handleViewItemDetails(item)} sx={{ color: '#667eea' }}>
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50]}
                      component="div"
                      count={filteredItems.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    />
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <TodayIcon sx={{ color: '#667eea' }} />
                      <Typography variant="h6" fontWeight={600}>
                        {startDate.toDateString() === endDate.toDateString() 
                          ? `Orders for ${startDate.toLocaleDateString()}` 
                          : `Orders ${getDateRangeText()}`}
                      </Typography>
                      <Chip label={`${todayTransactions.length} orders`} size="small" sx={{ bgcolor: '#667eea', color: 'white' }} />
                    </Box>
                    
                    {todayTransactions.length === 0 ? (
                      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                        <Typography variant="body1" color="text.secondary">No transactions for the selected date range</Typography>
                      </Paper>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                              <TableCell>Date & Time</TableCell>
                              <TableCell>Order #</TableCell>
                              <TableCell>Customer / Location</TableCell>
                              <TableCell>Items</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell>Staff</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {todayTransactions.map((tx) => (
                              <TableRow key={tx.id} hover>
                                <TableCell>{new Date(tx.date).toLocaleString()}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontFamily="monospace" fontWeight={500}>{tx.reference}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" fontWeight={500}>
                                      {tx.customer_name || 'Walk-in Customer'}
                                    </Typography>
                                    {tx.location && tx.location !== (tx.customer_name || 'Walk-in Customer') && (
                                      <Typography variant="caption" color="text.secondary">
                                        {tx.location}
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={tx.items}>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                      {tx.items?.split(',').slice(0, 2).join(', ')}{tx.items?.split(',').length > 2 ? '...' : ''}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={tx.order_type?.replace('_', ' ')} 
                                    size="small" 
                                    sx={{ 
                                      textTransform: 'capitalize', 
                                      bgcolor: tx.order_type === 'room_service' ? alpha('#10B981', 0.1) : 
                                               tx.order_type === 'Lounge' ? alpha('#8B5CF6', 0.1) :
                                               tx.order_type === 'dine_in' ? alpha('#F59E0B', 0.1) :
                                               alpha('#667eea', 0.1),
                                      color: tx.order_type === 'room_service' ? '#10B981' : 
                                             tx.order_type === 'Lounge' ? '#8B5CF6' :
                                             tx.order_type === 'dine_in' ? '#F59E0B' : '#667eea'
                                    }} 
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={tx.status} 
                                    size="small" 
                                    sx={{ 
                                      bgcolor: tx.status === 'completed' ? alpha('#10B981', 0.1) : 
                                               tx.status === 'confirmed' ? alpha('#3B82F6', 0.1) :
                                               tx.status === 'served' ? alpha('#8B5CF6', 0.1) :
                                               alpha('#F59E0B', 0.1), 
                                      color: tx.status === 'completed' ? '#10B981' : 
                                             tx.status === 'confirmed' ? '#3B82F6' :
                                             tx.status === 'served' ? '#8B5CF6' : '#F59E0B' 
                                    }} 
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={700}>₦{parseFloat(tx.amount).toLocaleString()}</Typography>
                                </TableCell>
                                <TableCell>{tx.processed_by || 'System'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}

                {activeTab === 2 && (
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Top 10 Best Selling Items {getDateRangeText()}
                    </Typography>
                    <Grid container spacing={2}>
                      {topSellingItems.length === 0 ? (
                        <Grid item xs={12}>
                          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                            <Typography variant="body1" color="text.secondary">No sales data available for the selected date range</Typography>
                          </Paper>
                        </Grid>
                      ) : (
                        topSellingItems.map((item, idx) => (
                          <Grid item xs={12} md={6} key={idx}>
                            <Card sx={{ borderRadius: 3, '&:hover': { boxShadow: '0 8px 25px rgba(0,0,0,0.1)', transform: 'translateY(-2px)', transition: 'all 0.3s' } }}>
                              <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: alpha('#667eea', 0.1), color: '#667eea', fontWeight: 700 }}>#{idx + 1}</Avatar>
                                    <Box>
                                      <Typography variant="subtitle1" fontWeight={700}>{item.item_name}</Typography>
                                      <Chip label={item.category} size="small" sx={{ mt: 0.5, bgcolor: alpha(CATEGORY_COLORS[item.category] || CATEGORY_COLORS.default, 0.1), color: CATEGORY_COLORS[item.category] || CATEGORY_COLORS.default }} />
                                    </Box>
                                  </Box>
                                  <Box sx={{ textAlign: 'right' }}>
                                    <Typography variant="h6" fontWeight={800} color="primary.main">₦{item.total_revenue?.toLocaleString()}</Typography>
                                    <Typography variant="caption" color="text.secondary">{item.total_quantity} units sold</Typography>
                                  </Box>
                                </Box>
                                <Divider sx={{ my: 1.5 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <Typography variant="body2" color="text.secondary">Current Stock:</Typography>
                                  <Typography variant="body2" fontWeight={600}>{item.current_stock} {item.unit}</Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))
                      )}
                    </Grid>
                  </Box>
                )}

                {activeTab === 3 && (
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Category Analysis {getDateRangeText()}
                    </Typography>
                    {categoryBreakdown.length === 0 ? (
                      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                        <Typography variant="body1" color="text.secondary">No category data available for the selected date range</Typography>
                      </Paper>
                    ) : (
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Revenue by Category</Typography>
                          <Box sx={{ height: 350 }}>
                            <Doughnut 
                              data={{
                                labels: categoryBreakdown.map(c => c.category_name),
                                datasets: [{
                                  data: categoryBreakdown.map(c => c.total_revenue),
                                  backgroundColor: categoryBreakdown.map(c => CATEGORY_COLORS[c.category_name] || CATEGORY_COLORS.default),
                                  borderWidth: 0,
                                }]
                              }}
                              options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Performance by Category</Typography>
                          <Stack spacing={2}>
                            {categoryBreakdown.map((cat, idx) => (
                              <Paper key={idx} sx={{ p: 2, borderRadius: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ bgcolor: alpha(CATEGORY_COLORS[cat.category_name] || CATEGORY_COLORS.default, 0.1), color: CATEGORY_COLORS[cat.category_name] || CATEGORY_COLORS.default, width: 32, height: 32 }}>
                                      {getCategoryIcon(cat.category_name)}
                                    </Avatar>
                                    <Typography variant="subtitle2" fontWeight={600}>{cat.category_name}</Typography>
                                  </Box>
                                  <Typography variant="h6" fontWeight={700}>₦{cat.total_revenue?.toLocaleString()}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary">Sold: {cat.total_quantity} items</Typography>
                                  <Typography variant="caption" color="text.secondary">Avg: ₦{cat.avg_price?.toLocaleString()}</Typography>
                                </Box>
                                <LinearProgress variant="determinate" value={summary.total_revenue ? (cat.total_revenue / summary.total_revenue) * 100 : 0} sx={{ height: 6, borderRadius: 3, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: CATEGORY_COLORS[cat.category_name] || CATEGORY_COLORS.default, borderRadius: 3 } }} />
                              </Paper>
                            ))}
                          </Stack>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                )}

                {activeTab === 4 && (
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Order Outlet Performance {getDateRangeText()}
                    </Typography>
                    
                    {orderTypeReport.length === 0 ? (
                      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                        <Typography variant="body1" color="text.secondary">No order data available for the selected date range</Typography>
                      </Paper>
                    ) : (
                      <>
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                          {orderTypeReport.map((type) => (
                            <Grid item xs={12} sm={6} md={2.4} key={type.order_type}>
                              <Zoom in>
                                <Paper 
                                  sx={{ 
                                    p: 2, 
                                    borderRadius: 3, 
                                    borderTop: `4px solid ${type.color}`,
                                    background: `linear-gradient(135deg, white 0%, ${type.light_color} 100%)`,
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
                                  }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Avatar sx={{ bgcolor: alpha(type.color, 0.1), color: type.color, width: 40, height: 40 }}>
                                      {getOrderTypeIcon(type.order_type)}
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={800} color={type.color}>
                                      {type.display_name}
                                    </Typography>
                                  </Box>
                                  <Typography variant="h5" fontWeight={800}>₦{type.total_revenue.toLocaleString()}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {type.total_orders} orders • {type.total_items_sold} items
                                  </Typography>
                                </Paper>
                              </Zoom>
                            </Grid>
                          ))}
                        </Grid>

                        <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                  <TableCell>Order Type</TableCell>
                                  <TableCell align="right">Total Orders</TableCell>
                                  <TableCell align="right">Total Items Sold</TableCell>
                                  <TableCell align="right">Total Revenue</TableCell>
                                  <TableCell align="right">Avg Order Value</TableCell>
                                  <TableCell align="right">Avg Items/Order</TableCell>
                                  <TableCell align="right">% of Total</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {orderTypeReport.map((type) => {
                                  const totalRevenue = summary.total_revenue || 1;
                                  const percentage = (type.total_revenue / totalRevenue) * 100;
                                  
                                  return (
                                    <TableRow key={type.order_type} hover>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                          <Avatar sx={{ bgcolor: alpha(type.color, 0.1), color: type.color, width: 32, height: 32 }}>
                                            {getOrderTypeIcon(type.order_type)}
                                          </Avatar>
                                          <Typography variant="subtitle2" fontWeight={600}>
                                            {type.display_name}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight={500}>
                                          {type.total_orders}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight={500}>
                                          {type.total_items_sold}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight={700} color="primary.main">
                                          ₦{type.total_revenue.toLocaleString()}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2">
                                          ₦{type.avg_order_value.toLocaleString()}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2">
                                          {type.avg_items_per_order}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                          <Typography variant="body2" fontWeight={500}>
                                            {percentage.toFixed(1)}%
                                          </Typography>
                                          <LinearProgress 
                                            variant="determinate" 
                                            value={percentage} 
                                            sx={{ 
                                              width: 60, 
                                              height: 6, 
                                              borderRadius: 3,
                                              bgcolor: alpha(type.color, 0.2),
                                              '& .MuiLinearProgress-bar': { bgcolor: type.color, borderRadius: 3 }
                                            }} 
                                          />
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>

                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Revenue Distribution by Order Type
                              </Typography>
                              <Box sx={{ height: 300 }}>
                                <Doughnut 
                                  data={{
                                    labels: orderTypeReport.map(t => t.display_name),
                                    datasets: [{
                                      data: orderTypeReport.map(t => t.total_revenue),
                                      backgroundColor: orderTypeReport.map(t => t.color),
                                      borderWidth: 0,
                                    }]
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      legend: { position: 'bottom' },
                                      tooltip: {
                                        callbacks: {
                                          label: (context) => {
                                            const label = context.label || '';
                                            const value = context.raw || 0;
                                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                            const percentage = ((value / total) * 100).toFixed(1);
                                            return `${label}: ₦${value.toLocaleString()} (${percentage}%)`;
                                          }
                                        }
                                      }
                                    }
                                  }}
                                />
                              </Box>
                            </Paper>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Items Sold by Order Type
                              </Typography>
                              <Box sx={{ height: 300 }}>
                                <Doughnut 
                                  data={{
                                    labels: orderTypeReport.map(t => t.display_name),
                                    datasets: [{
                                      data: orderTypeReport.map(t => t.total_items_sold),
                                      backgroundColor: orderTypeReport.map(t => t.color),
                                      borderWidth: 0,
                                    }]
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      legend: { position: 'bottom' },
                                      tooltip: {
                                        callbacks: {
                                          label: (context) => {
                                            const label = context.label || '';
                                            const value = context.raw || 0;
                                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                            const percentage = ((value / total) * 100).toFixed(1);
                                            return `${label}: ${value.toLocaleString()} items (${percentage}%)`;
                                          }
                                        }
                                      }
                                    }
                                  }}
                                />
                              </Box>
                            </Paper>
                          </Grid>
                        </Grid>

                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                              const exportData = orderTypeReport.map(type => ({
                                'Order Type': type.display_name,
                                'Total Orders': type.total_orders,
                                'Total Items Sold': type.total_items_sold,
                                'Total Revenue': type.total_revenue,
                                'Average Order Value': type.avg_order_value,
                                'Average Items Per Order': type.avg_items_per_order,
                                'Percentage of Total': ((type.total_revenue / summary.total_revenue) * 100).toFixed(2) + '%'
                              }));
                              const ws = XLSX.utils.json_to_sheet(exportData);
                              const wb = XLSX.utils.book_new();
                              XLSX.utils.book_append_sheet(wb, ws, 'Order_Type_Report');
                              XLSX.writeFile(wb, `order_type_report_${new Date().toISOString().split('T')[0]}.xlsx`);
                            }}
                            sx={{ borderRadius: 2 }}
                          >
                            Export Report
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}

                {activeTab === 5 && (
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Payment Methods Analysis {getDateRangeText()}
                    </Typography>
                    
                    {paymentMethodsReport.length === 0 ? (
                      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                        <Typography variant="body1" color="text.secondary">No payment data available for the selected date range</Typography>
                      </Paper>
                    ) : (
                      <>
                        {/* Summary Cards for Payment Methods */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                          {paymentMethodsReport.map((method) => {
                            const totalAmount = paymentMethodsReport.reduce((sum, m) => sum + m.total_amount, 0);
                            const percentage = (method.total_amount / totalAmount) * 100;
                            return (
                              <Grid item xs={12} sm={6} md={4} lg={2.4} key={method.payment_method}>
                                <Zoom in>
                                  <Paper 
                                    sx={{ 
                                      p: 2, 
                                      borderRadius: 3, 
                                      borderTop: `4px solid ${method.color}`,
                                      background: `linear-gradient(135deg, white 0%, ${method.light_color} 100%)`,
                                      boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                      <Avatar sx={{ bgcolor: alpha(method.color, 0.1), color: method.color, width: 40, height: 40 }}>
                                        {getPaymentMethodIcon(method.payment_method)}
                                      </Avatar>
                                      <Typography variant="subtitle2" fontWeight={700} color={method.color}>
                                        {method.payment_method}
                                      </Typography>
                                    </Box>
                                    <Typography variant="h5" fontWeight={800}>₦{method.total_amount.toLocaleString()}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {method.total_transactions} transactions
                                    </Typography>
                                    <LinearProgress 
                                      variant="determinate" 
                                      value={percentage} 
                                      sx={{ 
                                        mt: 1, 
                                        height: 4, 
                                        borderRadius: 3,
                                        bgcolor: alpha(method.color, 0.2),
                                        '& .MuiLinearProgress-bar': { bgcolor: method.color, borderRadius: 3 }
                                      }} 
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                      {percentage.toFixed(1)}% of total
                                    </Typography>
                                  </Paper>
                                </Zoom>
                              </Grid>
                            );
                          })}
                        </Grid>

                        {/* Main Table */}
                        <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 3 }}>
                          <TableContainer>
                            <Table>
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                  <TableCell>Payment Method</TableCell>
                                  <TableCell align="right">Transactions</TableCell>
                                  <TableCell align="right">Total Amount</TableCell>
                                  <TableCell align="right">Average Transaction</TableCell>
                                  <TableCell align="right">% of Total</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {paymentMethodsReport.map((method) => {
                                  const totalAmount = paymentMethodsReport.reduce((sum, m) => sum + m.total_amount, 0);
                                  const percentage = (method.total_amount / totalAmount) * 100;
                                  
                                  return (
                                    <TableRow key={method.payment_method} hover>
                                      <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                          <Avatar sx={{ bgcolor: alpha(method.color, 0.1), color: method.color, width: 32, height: 32 }}>
                                            {getPaymentMethodIcon(method.payment_method)}
                                          </Avatar>
                                          <Typography variant="subtitle2" fontWeight={600}>
                                            {method.payment_method}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight={500}>
                                          {method.total_transactions}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight={700} color="primary.main">
                                          ₦{method.total_amount.toLocaleString()}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2">
                                          ₦{method.avg_transaction_value.toLocaleString()}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                                          <Typography variant="body2" fontWeight={500}>
                                            {percentage.toFixed(1)}%
                                          </Typography>
                                          <LinearProgress 
                                            variant="determinate" 
                                            value={percentage} 
                                            sx={{ 
                                              width: 60, 
                                              height: 6, 
                                              borderRadius: 3,
                                              bgcolor: alpha(method.color, 0.2),
                                              '& .MuiLinearProgress-bar': { bgcolor: method.color, borderRadius: 3 }
                                            }} 
                                          />
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>

                        {/* Charts */}
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Revenue Distribution by Payment Method
                              </Typography>
                              <Box sx={{ height: 300 }}>
                                <Doughnut 
                                  data={{
                                    labels: paymentMethodsReport.map(m => m.payment_method),
                                    datasets: [{
                                      data: paymentMethodsReport.map(m => m.total_amount),
                                      backgroundColor: paymentMethodsReport.map(m => m.color),
                                      borderWidth: 0,
                                    }]
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      legend: { position: 'bottom' },
                                      tooltip: {
                                        callbacks: {
                                          label: (context) => {
                                            const label = context.label || '';
                                            const value = context.raw || 0;
                                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                            const percentage = ((value / total) * 100).toFixed(1);
                                            return `${label}: ₦${value.toLocaleString()} (${percentage}%)`;
                                          }
                                        }
                                      }
                                    }
                                  }}
                                />
                              </Box>
                            </Paper>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                Transactions by Payment Method
                              </Typography>
                              <Box sx={{ height: 300 }}>
                                <Doughnut 
                                  data={{
                                    labels: paymentMethodsReport.map(m => m.payment_method),
                                    datasets: [{
                                      data: paymentMethodsReport.map(m => m.total_transactions),
                                      backgroundColor: paymentMethodsReport.map(m => m.color),
                                      borderWidth: 0,
                                    }]
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      legend: { position: 'bottom' },
                                      tooltip: {
                                        callbacks: {
                                          label: (context) => {
                                            const label = context.label || '';
                                            const value = context.raw || 0;
                                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                                            const percentage = ((value / total) * 100).toFixed(1);
                                            return `${label}: ${value.toLocaleString()} transactions (${percentage}%)`;
                                          }
                                        }
                                      }
                                    }
                                  }}
                                />
                              </Box>
                            </Paper>
                          </Grid>
                        </Grid>

                        {/* Export Button */}
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => {
                              const totalAmount = paymentMethodsReport.reduce((sum, m) => sum + m.total_amount, 0);
                              const exportData = paymentMethodsReport.map(method => ({
                                'Payment Method': method.payment_method,
                                'Number of Transactions': method.total_transactions,
                                'Total Amount': method.total_amount,
                                'Average Transaction Value': method.avg_transaction_value,
                                'Percentage of Total': ((method.total_amount / totalAmount) * 100).toFixed(2) + '%'
                              }));
                              const ws = XLSX.utils.json_to_sheet(exportData);
                              const wb = XLSX.utils.book_new();
                              XLSX.utils.book_append_sheet(wb, ws, 'Payment_Methods_Report');
                              XLSX.writeFile(wb, `payment_methods_report_${new Date().toISOString().split('T')[0]}.xlsx`);
                            }}
                            sx={{ borderRadius: 2 }}
                          >
                            Export Report
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}
              </Paper>
            </>
          )}
        </Box>
      </Box>

      <Dialog open={itemDetailsOpen} onClose={() => setItemDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={700}>{selectedItem?.item_name}</Typography>
            <IconButton onClick={() => setItemDetailsOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedItem && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                <Chip label={selectedItem.category} sx={{ mt: 0.5 }} />
                
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Stock Info</Typography>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Current Stock:</Typography>
                    <Typography variant="body2" fontWeight={700}>{selectedItem.current_stock} {selectedItem.unit}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Minimum Stock:</Typography>
                    <Typography variant="body2">{selectedItem.min_stock} {selectedItem.unit}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Total Sold {getDateRangeText()}:</Typography>
                    <Typography variant="body2" fontWeight={500}>{selectedItem.total_sold_quantity || 0} units</Typography>
                  </Box>
                </Paper>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>Pricing</Typography>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  {selectedItem.price_dine_in && <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography variant="body2">Dine-in:</Typography><Typography fontWeight={500}>₦{selectedItem.price_dine_in.toLocaleString()}</Typography></Box>}
                  {selectedItem.price_room_service && <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography variant="body2">Room Service:</Typography><Typography fontWeight={500}>₦{selectedItem.price_room_service.toLocaleString()}</Typography></Box>}
                  {selectedItem.price_take_away && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">Takeaway:</Typography><Typography fontWeight={500}>₦{selectedItem.price_take_away.toLocaleString()}</Typography></Box>}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Sales Performance {getDateRangeText()}</Typography>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Total Revenue:</Typography>
                    <Typography variant="body2" fontWeight={700} color="primary.main">₦{(parseFloat(selectedItem.total_sold_amount) || 0).toLocaleString()}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Average Sale:</Typography>
                    <Typography variant="body2">₦{selectedItem.total_sold_quantity ? (parseFloat(selectedItem.total_sold_amount) / selectedItem.total_sold_quantity).toLocaleString() : 0}</Typography>
                  </Box>
                </Paper>

                <Typography variant="subtitle2" color="text.secondary">30-Day Trend</Typography>
                <Box sx={{ height: 200 }}>
                  {historyLoading ? <CircularProgress size={30} /> : salesHistory.length > 0 ? (
                    <Line data={{ labels: salesHistory.map(s => s.sale_date), datasets: [{ label: 'Revenue', data: salesHistory.map(s => s.revenue), borderColor: '#667eea', backgroundColor: alpha('#667eea', 0.1), fill: true, tension: 0.4 }] }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }} />
                  ) : <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No sales data available</Typography>}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setItemDetailsOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default InventoryAccounting;